import React, { useState } from "react";
import { CheckCircle2, Mail, Phone } from "lucide-react";
import { toast } from "react-toastify";
import { Account, accountApi, accountErr } from "../../api/account/accountApi";
import CodeModal from "./CodeModal";

interface ContactSectionProps {
  account: Account;
  onChanged: (account: Account) => void;
}

type Channel = "email" | "phone";

/**
 * Email and phone live on the Zitadel user. Email is the login identity and
 * is set by an administrator — users can only verify it here. Phone can be
 * changed: the new value is set UNVERIFIED and Zitadel texts a code to it;
 * the code modal completes the change. SMS-based 2FA needs a verified phone,
 * email codes a verified email — the Security tab points back here.
 */
const ContactSection: React.FC<ContactSectionProps> = ({ account, onChanged }) => {
  const [editing, setEditing] = useState<Channel | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState<Channel | null>(null);

  const startEditPhone = () => {
    setEditing("phone");
    setDraft(account.phone || "");
  };

  const submitPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = draft.trim();
    if (!value) return;
    if (value === account.phone) {
      setEditing(null);
      return;
    }
    setBusy(true);
    try {
      const r = await accountApi.changePhone(value);
      toast.info(r.data.message);
      // Reflect the pending value locally so the row shows what is being verified.
      onChanged({ ...account, phone: value, phone_verified: false });
      setVerifying("phone");
      setEditing(null);
    } catch (err) {
      toast.error(accountErr(err, "Could not update phone"));
    } finally {
      setBusy(false);
    }
  };

  const removePhone = async () => {
    if (!window.confirm("Remove your phone number? SMS codes will stop working.")) return;
    setBusy(true);
    try {
      const r = await accountApi.removePhone();
      onChanged(r.data);
      toast.success("Phone number removed");
    } catch (err) {
      toast.error(accountErr(err, "Could not remove phone"));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (code: string) => {
    const r =
      verifying === "email"
        ? await accountApi.verifyEmail(code)
        : await accountApi.verifyPhone(code);
    onChanged(r.data);
    setVerifying(null);
    toast.success(verifying === "email" ? "Email verified" : "Phone verified");
  };

  const resend = async () => {
    if (verifying === "email") await accountApi.resendEmailCode();
    else await accountApi.resendPhoneCode();
  };

  const Verified: React.FC<{ ok: boolean; present: boolean }> = ({ ok, present }) => {
    if (!present) return <span className="acct-badge acct-badge--muted">Not set</span>;
    return ok ? (
      <span className="acct-badge acct-badge--ok">
        <CheckCircle2 size={12} /> Verified
      </span>
    ) : (
      <span className="acct-badge acct-badge--warn">Unverified</span>
    );
  };

  return (
    <div className="acct-card">
      <div className="acct-card__head">
        <div>
          <h3>Contact details</h3>
          <p>Used for sign-in notifications, verification codes and 2FA.</p>
        </div>
      </div>

      {/* ---------------------------------------------------------- email */}
      <div className="acct-row">
        <div className="acct-row__main">
          <div className="acct-row__icon">
            <Mail size={18} />
          </div>
          <div>
            <div className="acct-row__label">
              Email <Verified ok={account.email_verified} present={!!account.email} />
            </div>
            <div className="acct-row__value">{account.email || "—"}</div>
            <div className="acct-row__hint">
              Your sign-in email is managed by your administrator.
            </div>
          </div>
        </div>
        <div className="acct-row__actions">
          {account.email && !account.email_verified && (
            <button
              className="acct-btn acct-btn--small"
              onClick={() => setVerifying("email")}
              disabled={busy}
            >
              Enter code
            </button>
          )}
        </div>
      </div>

      {/* ---------------------------------------------------------- phone */}
      <div className="acct-row">
        <div className="acct-row__main">
          <div className="acct-row__icon">
            <Phone size={18} />
          </div>
          <div>
            <div className="acct-row__label">
              Phone <Verified ok={account.phone_verified} present={!!account.phone} />
            </div>
            <div className="acct-row__value">{account.phone || "—"}</div>
            {!account.phone && (
              <div className="acct-row__hint">Needed for SMS verification codes.</div>
            )}
          </div>
        </div>
        <div className="acct-row__actions">
          {account.phone && !account.phone_verified && (
            <button
              className="acct-btn acct-btn--small"
              onClick={() => setVerifying("phone")}
              disabled={busy}
            >
              Enter code
            </button>
          )}
          <button
            className="acct-btn acct-btn--small"
            onClick={() => (editing === "phone" ? setEditing(null) : startEditPhone())}
            disabled={busy}
          >
            {editing === "phone" ? "Cancel" : account.phone ? "Change" : "Add"}
          </button>
          {account.phone && (
            <button
              className="acct-btn acct-btn--small acct-btn--danger"
              onClick={removePhone}
              disabled={busy}
            >
              Remove
            </button>
          )}
        </div>
        {editing === "phone" && (
          <form className="acct-inline" onSubmit={submitPhone}>
            <input
              type="tel"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="+91 98765 43210"
              required
            />
            <button type="submit" className="acct-btn acct-btn--primary acct-btn--small" disabled={busy}>
              {busy ? "Sending..." : "Send code"}
            </button>
          </form>
        )}
      </div>

      {verifying && (
        <CodeModal
          title={verifying === "email" ? "Verify your email" : "Verify your phone"}
          description={
            verifying === "email"
              ? `We sent a code to ${account.email}. Enter it below to confirm the address.`
              : `We sent a code by SMS to ${account.phone}. Enter it below to confirm the number.`
          }
          onVerify={verify}
          onResend={resend}
          onClose={() => setVerifying(null)}
        />
      )}
    </div>
  );
};

export default ContactSection;
